<!--
 * Path: src/features/browser/forms/DraggableTable.vue
 * Task: Reusable component providing drag & drop reordering wrapped inside a native Vuetify table.
 * Update: Completely stripped custom CSS table styling to use native v-table design standards.
-->
<template>
  <v-card variant="outlined" class="bg-surface">
    <v-table hover density="comfortable">
      <thead>
      <tr>
        <!-- Handle Column Header -->
        <th style="width: 48px;" class="text-center bg-surface">
          <v-icon size="small" color="medium-emphasis">mdi-swap-vertical</v-icon>
        </th>
        <!-- Dynamic Headers -->
        <th
          v-for="header in headers"
          :key="header.key"
          class="text-left text-caption font-weight-bold text-uppercase bg-surface"
          :style="{ width: header.width || 'auto' }"
        >
          {{ header.title }}
        </th>
      </tr>
      </thead>

      <!-- Vue Draggable Integration -->
      <draggable
        v-model="modelValue"
        tag="tbody"
        item-key="id"
        handle=".drag-handle"
        ghost-class="ghost-row"
        drag-class="dragging-row"
        animation="200"
      >
        <template #item="{ element }">
          <tr class="transition-swing">
            <td class="text-center px-0" style="vertical-align: middle;">
              <v-btn icon variant="text" size="small" class="drag-handle cursor-grab" color="medium-emphasis">
                <v-icon>mdi-drag</v-icon>
              </v-btn>
            </td>

            <td
              v-for="header in headers"
              :key="header.key"
              class="py-3 px-4"
              style="vertical-align: middle;"
            >
              <slot :name="'item.' + header.key" :item="element">
                {{ element[header.key] }}
              </slot>
            </td>
          </tr>
        </template>
      </draggable>
    </v-table>
  </v-card>
</template>

<script setup lang="ts">
import draggable from 'vuedraggable';

export interface TableHeader {
  title: string;
  key: string;
  width?: string;
}

const modelValue = defineModel<any[]>({ default: () => [] });

defineProps<{
  headers: TableHeader[];
}>();
</script>

<style scoped>
.cursor-grab {
  cursor: grab;
}
.cursor-grab:active {
  cursor: grabbing;
}
.ghost-row {
  opacity: 0.4;
  background-color: rgba(var(--v-theme-primary), 0.1);
}
.dragging-row {
  background-color: rgb(var(--v-theme-surface));
  box-shadow: 0 5px 15px rgba(0,0,0,0.15) !important;
}
</style>
